import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';

export const HomeGradientBackground = ({ children }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dimensions.width === 0) return;
    const ctx = canvas.getContext('2d');
    const { width, height } = dimensions;

    canvas.width = width;
    canvas.height = height;

    ctx.clearRect(0, 0, width, height);

    const scaffoldBg = isDark ? '#0D1117' : '#FFFFFF';

    const bgColors = isDark
      ? [scaffoldBg, '#070B12', '#010204']
      : [scaffoldBg, '#EDF1F6', '#E2E7ED'];

    const baseGradient = ctx.createLinearGradient(0, 0, 0, height);
    baseGradient.addColorStop(0, bgColors[0]);
    baseGradient.addColorStop(0.5, bgColors[1]);
    baseGradient.addColorStop(1, bgColors[2]);
    ctx.fillStyle = baseGradient;
    ctx.fillRect(0, 0, width, height);

    const gridColor = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
    const gridAccent = isDark ? 'rgba(31, 111, 235, 0.15)' : 'rgba(9, 105, 218, 0.18)';

    const smallStep = 56;
    const largeStep = smallStep * 4;

    for (let x = 0; x <= width; x += smallStep) {
      const isLarge = Math.round(x) % largeStep === 0;
      ctx.strokeStyle = isLarge ? gridAccent : gridColor;
      ctx.lineWidth = isLarge ? 0.75 : 0.35;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    for (let y = 0; y <= height; y += smallStep) {
      const isLarge = Math.round(y) % largeStep === 0;
      ctx.strokeStyle = isLarge ? gridAccent : gridColor;
      ctx.lineWidth = isLarge ? 0.75 : 0.35;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    const vignette = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, Math.max(width, height) * 1.1);
    vignette.addColorStop(0, 'transparent');
    vignette.addColorStop(0.55, isDark ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.01)');
    vignette.addColorStop(1, isDark ? 'rgba(0, 0, 0, 0.65)' : 'rgba(0, 0, 0, 0.08)');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, width, height);

  // إضافة isDark هنا تضمن إعادة مسح ورسم الـ Canvas فوراً عند تغيير الثيم
  }, [dimensions, isDark]);

  const maxDim = Math.max(dimensions.width, dimensions.height) || 1000;

  return (
    <div style={{ position: 'relative', width: '100%', minHeight: '100vh' }}>

      <canvas
        ref={canvasRef}
        style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', display: 'block', zIndex: 0, pointerEvents: 'none' }}
      />

      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1,
        pointerEvents: 'none',
        background: `linear-gradient(135deg, ${isDark ? 'rgba(31, 111, 235, 0.08)' : 'rgba(9, 105, 218, 0.12)'} 0%, transparent 50%, ${isDark ? 'rgba(158, 102, 255, 0.06)' : 'rgba(139, 70, 255, 0.10)'} 100%)`,
        mixBlendMode: isDark ? 'plus-lighter' : 'normal'
      }} />

      <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 2 }}>

        <div style={{
          position: 'absolute',
          left: '15%',
          top: '20%',
          width: `${maxDim * 0.96}px`,
          height: `${maxDim * 0.96}px`,
          transform: 'translate(-50%, -50%)',
          background: `radial-gradient(circle, ${isDark ? 'rgba(31, 111, 235, 0.32)' : 'rgba(9, 105, 218, 0.38)'} 0%, ${isDark ? 'rgba(31, 111, 235, 0.12)' : 'rgba(9, 105, 218, 0.15)'} 50%, transparent 100%)`,
          mixBlendMode: isDark ? 'plus-lighter' : 'normal',
          animation: 'floatCloud1 25s infinite ease-in-out',
          filter: 'blur(40px)'
        }} />

        <div style={{
          position: 'absolute',
          left: '85%',
          top: '25%',
          width: `${maxDim * 0.80}px`,
          height: `${maxDim * 0.80}px`,
          transform: 'translate(-50%, -50%)',
          background: `radial-gradient(circle, ${isDark ? 'rgba(84, 174, 255, 0.22)' : 'rgba(33, 142, 255, 0.30)'} 0%, ${isDark ? 'rgba(84, 174, 255, 0.08)' : 'rgba(33, 142, 255, 0.10)'} 50%, transparent 100%)`,
          mixBlendMode: isDark ? 'plus-lighter' : 'normal',
          animation: 'floatCloud2 25s infinite ease-in-out',
          filter: 'blur(40px)'
        }} />

        <div style={{
          position: 'absolute',
          left: '70%',
          top: '80%',
          width: `${maxDim * 1.1}px`,
          height: `${maxDim * 1.1}px`,
          transform: 'translate(-50%, -50%)',
          background: `radial-gradient(circle, ${isDark ? 'rgba(158, 102, 255, 0.28)' : 'rgba(139, 70, 255, 0.25)'} 0%, ${isDark ? 'rgba(158, 102, 255, 0.10)' : 'rgba(139, 70, 255, 0.11)'} 50%, transparent 100%)`,
          mixBlendMode: isDark ? 'plus-lighter' : 'normal',
          animation: 'floatCloud3 25s infinite ease-in-out',
          filter: 'blur(40px)'
        }} />

      </div>

      <style>{`
        @keyframes floatCloud1 {
          0%, 100% { transform: translate(-50%, -50%) translate(0px, 20px); }
          50% { transform: translate(-50%, -50%) translate(30px, -20px); }
        }
        @keyframes floatCloud2 {
          0%, 100% { transform: translate(-50%, -50%) translate(25px, 0px); }
          50% { transform: translate(-50%, -50%) translate(-25px, 25px); }
        }
        @keyframes floatCloud3 {
          0%, 100% { transform: translate(-50%, -50%) translate(0px, 30px); }
          50% { transform: translate(-50%, -50%) translate(35px, -30px); }
        }
      `}</style>

      <div style={{ position: 'relative', zIndex: 3, width: '100%' }}>
        {children}
      </div>
    </div>
  );
};
